import { PzModel } from '../model/pz-model';

export enum Routes {
  HOME = 'home',
  ERROR = 'error',
  SETTINGS = 'settings',
  STATISTICS = 'statistics',
  CROWNS = 'crowns',
  ADMIN = 'admin',
}

export enum Winner {
  PLAYER_1 = 'player1',
  PLAYER_2 = 'player2',
  BOTH = 'both',
}

export enum SoundFile {
  WIN = 'win.mp3',
  SENFKARTE_WIN = 'yes.mp3',
  EXTRA_WIN = 'extra-win.mp3',
  // FINAL_WIN = 'final-win.mp3',
  APPLAUSE = 'girls_applause.mp3',
  ERROR = 'error.mp3',
  DING = 'ding.mp3',
}

export type Result = {
  differenceToAmazonPricePlayer1: number;
  differenceToAmazonPricePlayer2: number;
  points: number;
  winner: Winner;
  sound: SoundFile;
  senfkarteUsed: boolean;
};

// Formatted price from external PZ plugin, e.g. {"pzPrice":"16,13","pzUrl":"https://www.amazon.de/Mundschutzmaske-zertifiziert-Atemschutzmasken-hygienische-Einzelverpackung/dp/B08TTYBD3K/ref=zg-bs_sports_1/258-4861201-5120361?pd_rd_w=agPfD&pf_rd_p=25d6d4f8-2717-4a48-97fc-9b1a75c8d109&pf_rd_r=AGQ7YQ3C8BRKKY2Y44RZ&pd_rd_r=388e12dc-70a6-4e6f-840b-77546d542c08&pd_rd_wg=nBLma&pd_rd_i=B08TTYBD3K&psc=1","pzTitle":"EUROPAPA® 20x FFP2 Schwarz Maske 5-Lagen Mundschutzmaske CE Stelle zertifiziert Atemschutzmasken hygienische Einzelverpackung EU 2016/425 : Amazon.de: Sport & Freizeit"}
export type PzPluginData = {
  pzPrice: string;
  pzUrl: string;
  pzTitle: string;
};

export const computeDifferenceToAmazonPrice = (
  amazonPrice: number,
  playerPrice: number
): number => {
  return Math.abs(playerPrice - amazonPrice);
};

export const isHoffenHeimSchaetzung = (model: PzModel): boolean =>
  model.estimatedPricePlayer1 === 18.99 ||
  model.estimatedPricePlayer2 === 18.99;

export const estimationsAreZeroOrSame = (model: PzModel): boolean => {
  return (
    model.estimatedPricePlayer1 === 0 ||
    model.estimatedPricePlayer2 === 0 ||
    model.estimatedPricePlayer1 === model.estimatedPricePlayer2
  );
};

export const computeResult = (model: PzModel): Result => {
  const {
    priceAmazon,
    estimatedPricePlayer1,
    estimatedPricePlayer2,
    senfkartePlayer1,
    senfkartePlayer2,
  } = model;

  if (senfkartePlayer1) {
    model.senfkartePlayer1Possible = false;
  }

  if (senfkartePlayer2) {
    model.senfkartePlayer2Possible = false;
  }

  const diffPlayer1Price = computeDifferenceToAmazonPrice(
    priceAmazon,
    estimatedPricePlayer1
  );
  const diffPlayer2Price = computeDifferenceToAmazonPrice(
    priceAmazon,
    estimatedPricePlayer2
  );
  const roundedAmazonPrice = Math.round(priceAmazon);

  // console.log('difference Player1: ', diffPlayer1Price);
  // console.log('difference Player2: ', diffPlayer2Price);

  if (diffPlayer1Price === 0 && diffPlayer2Price === 0) {
    return {
      differenceToAmazonPricePlayer1: diffPlayer1Price,
      differenceToAmazonPricePlayer2: diffPlayer2Price,
      points: roundedAmazonPrice,
      winner: Winner.BOTH,
      sound: SoundFile.EXTRA_WIN,
      senfkarteUsed: false,
    };
  }
  if (diffPlayer1Price === 0) {
    return {
      differenceToAmazonPricePlayer1: diffPlayer1Price,
      differenceToAmazonPricePlayer2: diffPlayer2Price,
      points: roundedAmazonPrice, // No multiplicator when using Senfkarte
      winner: Winner.PLAYER_1,
      sound: SoundFile.EXTRA_WIN,
      senfkarteUsed: senfkartePlayer1,
    };
  }
  if (diffPlayer2Price === 0) {
    return {
      differenceToAmazonPricePlayer1: diffPlayer1Price,
      differenceToAmazonPricePlayer2: diffPlayer2Price,
      points: roundedAmazonPrice, // No multiplicator when using Senfkarte
      winner: Winner.PLAYER_2,
      sound: SoundFile.EXTRA_WIN,
      senfkarteUsed: senfkartePlayer2,
    };
  }
  if (diffPlayer1Price === diffPlayer2Price) {
    return {
      differenceToAmazonPricePlayer1: diffPlayer1Price,
      differenceToAmazonPricePlayer2: diffPlayer2Price,
      points: 1,
      winner: Winner.BOTH,
      sound: SoundFile.WIN,
      senfkarteUsed: false,
    };
  }
  if (diffPlayer1Price < diffPlayer2Price) {
    return {
      differenceToAmazonPricePlayer1: diffPlayer1Price,
      differenceToAmazonPricePlayer2: diffPlayer2Price,
      points: senfkartePlayer1 ? 2 : 1,
      winner: Winner.PLAYER_1,
      sound: senfkartePlayer1 ? SoundFile.SENFKARTE_WIN : SoundFile.WIN,
      senfkarteUsed: senfkartePlayer1,
    };
  }
  return {
    differenceToAmazonPricePlayer1: diffPlayer1Price,
    differenceToAmazonPricePlayer2: diffPlayer2Price,
    points: senfkartePlayer2 ? 2 : 1,
    winner: Winner.PLAYER_2,
    sound: senfkartePlayer2 ? SoundFile.SENFKARTE_WIN : SoundFile.WIN,
    senfkarteUsed: senfkartePlayer2,
  };
};

export function getPriceFromPzPluginData(pzPluginPrice: PzPluginData): string {
  return pzPluginPrice.pzPrice;
}

export function getArticleUrlFromPzPluginData(
  pzPluginPrice: PzPluginData
): string {
  return pzPluginPrice.pzUrl;
}

export function getArticleTitleFromPzPluginData(
  pzPluginPrice: PzPluginData
): string {
  return pzPluginPrice.pzTitle;
}

// https://stackoverflow.com/a/56369438
// TODO: Maybe use decimal.js/big.js and format method https://www.npmjs.com/package/toformat
export function parseLocaleNumber(stringNumber: string): number {
  const num = 123456.789;
  const fmt_local = new Intl.NumberFormat();
  const parts_local = fmt_local.formatToParts(num);
  let group = ' ';
  let decimal = ',';

  parts_local.forEach(function (i) {
    switch (i.type) {
      case 'group':
        group = i.value;
        break;
      case 'decimal':
        decimal = i.value;
        break;
      default:
        break;
    }
  });

  return parseFloat(
    stringNumber
      .replace(new RegExp('\\' + group, 'g'), '')
      .replace(new RegExp('\\' + decimal), '.')
  );
}

// TODO: Use regex to detect repeated or multiple commas
export function replaceDoubleCommas(value: string): string {
  return value.replace(',,', ',');
}
